<template>
	<div class="cassie-vertical-md mt-1">
		<FilterBar
			:search-query.sync="cookieAccessKeySearchQuery"
			search-query-label="Name"
			:selected-brand-id.sync="selectedAdminPortalBrandId"
			@persistSearchQuery="changeCookieAccessKeySearchQuery"
			@persistSelectedBrandId="changeAdminPortalBrandFilter"
		>
			<template #action>
				<PrimaryActionButton
					v-if="userFullPermissions"
					@click="$emit('create-access-key', accessKeyType)"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					Create
				</PrimaryActionButton>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				Cookie Access Keys
			</template>
			<template #body>
				<DataTable
					:headers="tableHeaders"
					:items="filteredAccessKeys"
					sort-by="accessKeyId"
					sort-desc
					@click:row="editAccessKey"
				>
					<template #item.action="{ item }">
						<v-menu
							offset-y
							left
						>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									@click="copyAccessKey(item.accessKey)"
								>
									<v-list-item-title>
										Copy Key
									</v-list-item-title>
								</v-list-item>
								<v-list-item
									@click="editAccessKey(item)"
								>
									<v-list-item-title>
										{{ userFullPermissions && userHasBrand(item.brandId) ? 'Edit' : 'View' }}
									</v-list-item-title>
								</v-list-item>
								<v-list-item
									v-if="userFullPermissions && userHasBrand(item.brandId)"
									@click="cookieAccessKeyToRemove = item"
								>
									<v-list-item-title>
										Delete
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
		<ConfirmDeleteModal
			v-if="cookieAccessKeyToRemove"
			:entity-name="cookieAccessKeyToRemove.name"
			entity-type="Cookie Access Key"
			@close="cookieAccessKeyToRemove = null"
			@delete="removeKey"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import FilterBar from '../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import ConfirmDeleteModal from '../../../../../../shared/components/confirm-delete-modal.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { deleteCookieAccessKey } from '../../../../../../shared/utils/api/access-keys.js'
import { ACCESS_KEYS_MODULE_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { useLabels } from '../../../../../../shared/state/admin-portal-navigation.js'
import { changeCookieAccessKeySearchQuery, cookieAccessKeySearchQuery } from '../../../../../../shared/state/access-keys.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId, userHasBrand } from '../../../../../../shared/state/brands.js'
export default {
	components: { FilterBar, PrimaryActionButton, SectionCard, DataTable, ConfirmDeleteModal },
	props: {
		accessKeyType: {
			type: Number,
			required: true
		},
		accessKeys: {
			type: Array,
			default: () => []
		}
	},
	setup () {
		return {
			userHasBrand,
			changeCookieAccessKeySearchQuery,
			cookieAccessKeySearchQuery,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId
		}
	},
	data () {
		return {
			cookieAccessKeyToRemove: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		tableHeaders () {
			const tableHeaders = [
				{
					text: 'ID',
					value: 'accessKeyId',
					width: '5%'
				},
				{
					text: useLabels('Brand'),
					value: 'brandName',
					width: '15%'
				},
				{
					text: 'Name',
					value: 'name',
					width: '20%'
				},
				{
					text: 'Access Key',
					value: 'accessKey',
					width: '25%'
				},
				{
					text: 'Description',
					value: 'description',
					width: '25%'
				},
				{
					text: 'Created Date',
					value: 'createdDate',
					width: '10%'
				},
				{
					text: 'Action',
					value: 'action',
					width: '80px',
					align: 'center',
					sortable: false
				}
			]
			return tableHeaders
		},
		filteredAccessKeys () {
			return this.accessKeys.filter(({ name, brandId }) => {
				let check = true
				if (cookieAccessKeySearchQuery.value) check = name.toLowerCase().includes(cookieAccessKeySearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				return check
			}).map(accessKey => ({
				...accessKey,
				accessKey: (accessKey.accessKey || '').toUpperCase()
			}))
		},
		userFullPermissions () {
			return this.productAreaPermission(ACCESS_KEYS_MODULE_FULL_PERMISSIONS)
		}
	},
	methods: {
		editAccessKey (accessKeyToEdit) {
			const accessKeyData = {
				accessKeyToEdit,
				accessKeyType: this.accessKeyType
			}
			this.$emit('edit-access-key', accessKeyData)
		},
		async removeKey () {
			await deleteCookieAccessKey(this.cookieAccessKeyToRemove.accessKey)
			this.cookieAccessKeyToRemove = null
			this.$emit('load-access-keys')
			showSnackbar('This Access Key has been removed')
		},
		copyAccessKey (accessKey) {
			navigator.clipboard.writeText((accessKey || '').toUpperCase())
			showSnackbar('Access Key copied to clipboard')
		}
	}
}
</script>
